import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { CloseLineIcon, TicketRunningIcon } from '@/components/icons/svg-icons';
import { BetMsgModel, BET_TYPE, CricketPlayerBetTypeList, SPORT_TYPE, TicketModel } from '@/hooks/query';
import { useBetslipStore } from '@/store/betslip';
import { BetTicketOddsGroup } from '../bet-ticket-odds-group';
import { BetInput } from '../bet-input';
import { BetKeypad } from '../bet-keypad';
import { format } from 'date-fns';
import { TicketPayout } from '../payout';
import { TextError } from '../bet-msg';
import { useTranslations } from 'next-intl';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import { SingleMatchParlayTicketDetails } from '@/features/single-match-parlay/components/single-match-parlay-ticket-details';
import { useBetslipContext } from '@/features/betslip/contexts/betslip-context';
import { betTicketStyle } from './bet-ticket.style';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { BetslipBanner } from '@/features/promotion/golden-duel/components/betslip-banner';
import { useAsideBetslip } from '@/features/betslip/hook/use-aside-betslip';

const VoucherContainer = dynamic(() =>
	import('@/features/betslip/voucher/components/voucher-container').then(mod => mod.VoucherContainer)
);

interface BetTicketProps {
	ticket: TicketModel;
	inputCurrency?:string;
	ticketNumber?: number; // for parlay min odds
	canBetResult?: BetMsgModel;
	showTextError?: boolean;
	isEndingGameMultiTicket?: boolean;
	showBetInput?: boolean;
	showVoucher? : boolean;
}

export const BetTicket = ({ 
	ticket, 
	inputCurrency,
	ticketNumber, 
	canBetResult, 
	showBetInput,
	showVoucher = false, 
	showTextError = true,
}: BetTicketProps) => {
	const { isIndependentMode } = useBetslipContext();
	const { activeAsideBetSlip } = useAsideBetslip();
	const [
		removeTicket,
		canBetResultInnerMsg,
		betslipTab,
		betslipType
	] = useBetslipStore(
		state => [
			state.removeTicket,
			state.canBetResult.innerMsg,
			state.betslipTab,
			state.betslipType
		]
	);
	const isIndependentParlayMode = isIndependentMode && betslipTab === 'parlay';
	const canBet = canBetResult ? canBetResult.canBet : true;
	const showTicketNumber = (!isIndependentMode || isIndependentParlayMode) && canBetResultInnerMsg.find(msg => [60, 61].includes(msg.code));
	const isSmp = smpService.isSmpTicket(ticket);

	return (
		<div className={clsx('ticket', betTicketStyle.ticket)} data-disabled={!canBet}>
			<div className="ticket__wrap">
				{
					showTicketNumber
						? <div className="ticket__number">{ticketNumber}</div>
						: null
				}
				<div className="ticket__status">
					<button
						className="btn btn--icon"
						onClick={() => removeTicket(ticket.key, 'null')}
					>
						<CloseLineIcon />
					</button>
				</div>
				<div className="ticket__container">
					<div className="ticket__header">
						{isSmp
							? <div className="ticket__choice">{ticket.BettypeName}</div>
							: <TicketChoice ticket={ticket} />
						}
					</div>
					<div className="ticket__row">
						<SportBetTypeName ticket={ticket} />
						<Score ticket={ticket} />
					</div>
					<TeamName ticket={ticket} />

					{isSmp && ticket.specialChoices ? (
						<SingleMatchParlayTicketDetails choices={ticket.specialChoices} />
					) : null}

					{CricketPlayerBetTypeList.includes(Number(ticket.Bettype))
						? <CricketPlayerName ticket={ticket} />
						: <OddsInfo ticket={ticket} />
					}

					{showTextError && canBetResult ? (
						<TextError
							msg={canBetResult.msg}
							msgResourceKey={canBetResult.msgResourceKey}
						/>
					) : null}
				</div>
				<BetTicketOddsGroup
					ticket={ticket}
					canBetResult={canBetResult}
				/>
				{
					showBetInput && canBet && (!isIndependentParlayMode || betslipType === 'single') ? (
						<div className="ticket__stake">
							<BetInput
								type={'small'}
								inputCurrency={inputCurrency}
								isParlay={false}
								canBet={true}
								ticketKey={ticket.key}
								ticketMinBet={ticket.minBet}
							/>
							<TicketPayout
								payout={ticket.payout}
								betType={ticket.Bettype}
								betTeam={ticket.Betteam}
							/>
						</div>
					) : null
				}
			</div>
			{showVoucher ? <VoucherContainer tabType="tab" /> : null}
			{
				showBetInput && canBet && (!isIndependentParlayMode || betslipType === 'single') ?
					<BetKeypad
						isExpand={true}
						isParlay={false}
						ticketKey={ticket.key}
						ticketStake={ticket.stake}
						ticketMaxBet={ticket.maxBet}
					/> : null
			}

			{((betslipType === 'multi') || (betslipType === 'single' && activeAsideBetSlip)) ? (
				<BetslipBanner 
					ticket={ticket}
				/>
			) : null}
		</div>
	);
};

export const TicketChoice = ({ ticket }: BetTicketProps) => {
	const choiceValue = ticket.ChoiceValue;

	if (ticket.Bettype === BET_TYPE.PenaltyShootoutCombo) return null;

	let choiceClassName = '';
	if (ticket.SportType === SPORT_TYPE.MuayThai && ticket.Bettype === BET_TYPE.Hdp) {
		choiceClassName = ticket.Betteam === 'h' ? ' team-red' : ' team-blue';
	}

	return (
		<div className={clsx('ticket__choice', choiceClassName, betTicketStyle.choice)}>
			{choiceClassName ? <TicketRunningIcon /> : null}
			<span dangerouslySetInnerHTML={{ __html: choiceValue }}></span>
			<TicketGoal ticket={ticket} />
		</div>
	);
};

const TicketGoal = ({ ticket }: BetTicketProps) => {
	const dontShowHdpBetType = [BET_TYPE.TimeMachineHdp, BET_TYPE.TimeMachineOu, BET_TYPE.BaseBall_8825] as Array<string>;

	const displayHdp = dontShowHdpBetType.includes(ticket.Bettype) ? '' : ticket.DisplayHDP;

	return displayHdp
		? <span className={clsx('ticket__goal', betTicketStyle.goal)} data-change={ticket.isLineChange}>{displayHdp}</span>
		: null;
};

export const SportBetTypeName = ({ ticket }: BetTicketProps) => {
	const t = useTranslations('General');

	let betTypeName = ticket.BettypeName ?? '';
	const isSmp = smpService.isSmpTicket(ticket);
	const showBetTypeName = !isSmp;
	const { filterSabaString } = useFilterSabaString();


	if (ticket.SportType === SPORT_TYPE.Outright) {
		betTypeName = t('lbl_999');
	} else {
		let liveStr = '';

		if (ticket.IsLive && ticket.SportType !== SPORT_TYPE.NumberGame && ticket.SportType !== SPORT_TYPE.Happy5) {
			liveStr = ticket.IsInPlay ? t('lbl_Status_InPlay') : t('lbl_live');
		}

		if (ticket.SportType === SPORT_TYPE.ESports) {
			betTypeName = `${liveStr ? `${liveStr} `: ''}${filterSabaString(ticket.GameName, ticket.LeagueGroupId)} - ${ticket.BettypeName}`;
		} else {
			betTypeName = ticket.BettypeName ? liveStr + ' ' + ticket.BettypeName : '';
		}
	}

	return (
		<span className={clsx('ticket__item', betTicketStyle.bettype)}>
			{ticket.SportName}
			{showBetTypeName ? ` / ${betTypeName}` : null}
		</span>
	);
};

export const Score = ({ ticket }: BetTicketProps) => {
	const t = useTranslations('General');

	let score = '';

	if (ticket.BetID && (ticket.SportType === SPORT_TYPE.NumberGame || ticket.SportType === SPORT_TYPE.Happy5)) {
		score = `${t('lbl_NG_lastdrawn')}:${ticket.BetID}`;
	} else {
		if (ticket.IsLive && (ticket.LiveScore || ticket.Bettype === '38')) {
			score = `${ticket.LiveHomeScore}-${ticket.LiveAwayScore}`;
		}
	}

	return score ? <span className={clsx('ticket__item', betTicketStyle.score)}>{score}</span> : null;
}

export const TeamName = ({ ticket }: BetTicketProps) => {
	const isBitCoin = ticket.SportType === SPORT_TYPE.BitCoin;
	const isOutright = ticket.SportType === SPORT_TYPE.Outright;
	let content = null;

	if (isBitCoin) {
		const teamName = ticket.Matchid.toString();
		const endTimestamp = ticket.BetTimeConstraint.EndTimestamp;
		const displayTime = endTimestamp
			? format(new Date(endTimestamp * 1000), 'MM/dd/yyyy hh:mm aa')
			: '';

		content = (
			<>
				<div className={clsx('ticket__row', betTicketStyle.teamName)}>{teamName}</div>
				{isBitCoin ? <div className="ticket__row">{displayTime}</div> : null}
			</>
		);
	} else if (!isOutright) {
		const teamName = `${ticket.HomeName} vs ${ticket.AwayName}`;

		content = <div className={clsx('ticket__row', betTicketStyle.teamName)}>{teamName}</div>;
	}

	return content;
};

export const CricketPlayerName = ({ ticket }: BetTicketProps) => {
	return <div className="ticket__row">{ticket.OddsInfo}</div>;
};

export const OddsInfo = ({ ticket }: BetTicketProps) => {
	const t = useTranslations('General');

	const oddsInfo = ticket.OddsInfo ?? '';
	let info = null;
	const scoreBoxBetType = [BET_TYPE.ScoreBoxHdp, BET_TYPE.ScoreBoxOu] as Array<string>;

	if (oddsInfo !== '') {
		if (scoreBoxBetType.includes(ticket.Bettype)) {
			info = <div className="ticket__row" dangerouslySetInnerHTML={{ __html: (oddsInfo) }}></div>;
		} else {
			if (oddsInfo.indexOf('/') === 0) {
				info = <div className="ticket__row">{'AOS ' + t('pop_ft') + ' ' + t('lbl_excluding') + ': ' + oddsInfo.replace('/', '')}</div>;
			} else if (oddsInfo.indexOf('/') === (oddsInfo.length - 1)) {
				info = <div className="ticket__row">{'AOS ' + t('pop_ht') + ' ' + t('lbl_excluding') + ': ' + oddsInfo.replace('/', '')}</div>;
			} else if (oddsInfo.indexOf('/') > 1) {
				info = <div className="ticket__row">{'AOS ' + t('pop_ht') + ' ' + t('lbl_excluding') + ': ' + oddsInfo.split('/')[0]}</div>;
				info = <div className="ticket__row">{'AOS ' + t('pop_ft') + ' ' + t('lbl_excluding') + ': ' + oddsInfo.split('/')[1]}</div>;
			} else {
				info = <div className="ticket__row" dangerouslySetInnerHTML={{ __html: (t('lbl_excluding') + ': ' + oddsInfo) }}></div>;
			}
		}
	} else if (ticket.Bettype === BET_TYPE.PenaltyShootoutCombo) {
		info = <div className="ticket__row" dangerouslySetInnerHTML={{ __html: (ticket.ChoiceValue) }}></div>;
	}

	return info;
}
